import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { CreateInvitationResponse, MemberRole, PublicInvitation, PublicMember, SessionState }
  from '@mlc/shared';
import {
  cancelInvitation,
  closeSession,
  createInvitation,
  listInvitations,
  listMembers,
  readSession,
  revokeMemberSessions,
  updateMember,
} from './api';
import { purgerTracesLocales } from './memberScope';
import { rafraichirSession } from './session';
import { Banner, Button, Select, TextInput } from './ui';

/**
 * Compte et membres (US-42, vague E ; invitations en US-103).
 *
 * L'écran de gestion « complet » — celui qui accompagne la bascule de
 * visibilité, l'attribution des recettes et le tri en lot — arrive en US-49.
 * Ce bloc-ci n'existe que pour rendre US-42 utilisable seule : sans lui, la
 * seule façon de créer un deuxième compte serait d'écrire dans la base à la
 * main, et « déconnecter mes autres appareils » n'aurait aucun bouton.
 *
 * **« Ajouter quelqu'un » est devenu « Inviter quelqu'un » (US-103).** Le
 * formulaire demandait un *mot de passe provisoire* : le propriétaire
 * choisissait un secret pour quelqu'un d'autre, le lui transmettait comme il
 * pouvait, et rien ne garantissait qu'il soit changé un jour. L'invité pose
 * désormais le sien, et aucun mot de passe ne circule.
 */

const LIBELLES_ROLE: Record<MemberRole, string> = {
  proprietaire: 'Propriétaire',
  membre: 'Membre',
  lecture: 'Lecture seule',
};

function MemberSettingsInterne({ portee }: { portee: 'compte' | 'maison' }): React.JSX.Element {
  const [session, setSession] = useState<SessionState | null>(null);
  const [members, setMembers] = useState<PublicMember[]>([]);
  const [invitations, setInvitations] = useState<PublicInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const recharger = useCallback(() => {
    readSession()
      .then((etat) => {
        setSession(etat);
        return listMembers().then((liste) => ({ etat, liste }));
      })
      .then(({ etat, liste }) => {
        setMembers(liste);
        // Les invitations sont réservées au propriétaire : les demander pour
        // un autre membre ferait un 404 attendu, donc une bannière d'erreur
        // pour un écran qui fonctionne. On ne les demande pas.
        if (etat.member?.role !== 'proprietaire') { setInvitations([]); return; }
        listInvitations()
          .then(setInvitations)
          .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }, []);

  useEffect(recharger, [recharger]);

  const moi = session?.member ?? null;
  const proprietaire = moi?.role === 'proprietaire';

  function agir(promesse: Promise<unknown>, message: string): void {
    setError(null);
    promesse
      .then(() => { setNotice(message); recharger(); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  return (
    <div className="flex flex-col gap-4">
      {error !== null && <Banner tone="danger">{error}</Banner>}
      {notice !== null && <Banner tone="success">{notice}</Banner>}

      {portee === 'compte' && moi !== null && (
        <p className="text-sm text-foreground-secondary">
          Connecté en tant que <strong>{moi.display_name}</strong> · {LIBELLES_ROLE[moi.role]}
        </p>
      )}

      {portee === 'compte' && <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            if (moi === null) return;
            // Cas du téléphone perdu : une colonne, une ligne, aucun magasin de
            // sessions — et cela ne touche que ce membre.
            agir(revokeMemberSessions(moi.id), 'Vos autres appareils ont été déconnectés.');
          }}
        >
          Déconnecter mes autres appareils
        </Button>
        {/* `secondary` et non `ghost` (US-57) : c'était le geste le plus
            fréquent de la section, rendu comme un lien souligné, en seconde
            position d'une rangée en milieu d'écran. */}
        <Button
          variant="secondary"
          onClick={() => {
            // La déconnexion efface le cookie **et les traces locales**
            // (US-47) : sur la tablette de cuisine, une fiche privée restait
            // consultable hors connexion par le membre suivant.
            closeSession()
              .then(() => purgerTracesLocales())
              .catch(() => purgerTracesLocales())
              .finally(() => { window.location.reload(); });
          }}
        >
          Se déconnecter
        </Button>
      </div>}

      {portee === 'compte' && moi !== null && (
        <ProfileForm
          moi={moi}
          onDone={(message) => { setNotice(message); recharger(); }}
          onError={(message) => { setError(message); }}
        />
      )}

      {portee === 'compte' && (
        <ChangePassword
          onDone={(message) => { setNotice(message); }}
          onError={(message) => { setError(message); }}
          memberId={moi?.id ?? null}
        />
      )}

      {portee === 'maison' && <>
      <h3 className="text-heading-3">Membres</h3>
      <ul className="flex flex-col gap-2">
        {members.map((membre) => (
          <li
            key={membre.id}
            className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-surface-raised px-3 py-2"
          >
            <span className={membre.disabled_at === null ? '' : 'text-foreground-secondary line-through'}>
              {membre.display_name}
            </span>
            <span className="text-sm text-foreground-secondary">{LIBELLES_ROLE[membre.role]}</span>
            {/* L'adresse n'est renvoyée qu'à soi-même et au propriétaire
                (US-52) : elle est donc affichée quand elle est là, et rien
                n'est à masquer côté client — le serveur a déjà tranché. */}
            {membre.email !== null && (
              <span className="text-meta">{membre.email}</span>
            )}
            {proprietaire && membre.id !== moi?.id && (
              <span className="ml-auto flex gap-2">
                <Select
                  aria-label={`Rôle de ${membre.display_name}`}
                  value={membre.role}
                  onChange={(e) => {
                    agir(
                      updateMember(membre.id, { role: e.target.value as MemberRole }),
                      `Le rôle de ${membre.display_name} a été changé.`,
                    );
                  }}
                >
                  {(Object.keys(LIBELLES_ROLE) as MemberRole[]).map((role) => (
                    <option key={role} value={role}>{LIBELLES_ROLE[role]}</option>
                  ))}
                </Select>
                <Button
                  variant="ghost"
                  onClick={() => {
                    agir(
                      updateMember(membre.id, { disabled: membre.disabled_at === null }),
                      membre.disabled_at === null
                        ? `${membre.display_name} n’a plus accès au livre.`
                        : `${membre.display_name} a de nouveau accès au livre.`,
                    );
                  }}
                >
                  {membre.disabled_at === null ? 'Désactiver' : 'Réactiver'}
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>

      {/* On désactive, on ne supprime jamais : le principe de `recipe.archived`
          transposé aux personnes. Le dire à l'écran évite qu'on cherche un
          bouton « supprimer » qui n'existera pas. */}
      <p className="text-xs text-foreground-secondary">
        Un membre se désactive, il ne se supprime pas : ses recettes partagées restent dans le livre et son
        journal reste à son nom.
      </p>

      {proprietaire && (
        <InvitationsEnAttente
          invitations={invitations}
          onDone={(message) => { setNotice(message); recharger(); }}
          onError={setError}
        />
      )}

      {proprietaire && (
        <InviterQuelquun
          courrier={session?.courrier === true}
          onDone={(message) => { setNotice(message); recharger(); }}
          onError={setError}
        />
      )}
      </>}
    </div>
  );
}

/**
 * Mon compte (US-57) : profil, mot de passe, sessions, déconnexion.
 *
 * Séparé de la gestion des membres, qui vivait dans la même section, séparée
 * d'un simple `<h3>`. Ce sont deux natures : ce que je règle **pour moi**, et
 * ce que j'administre **pour la maison** — et seul le propriétaire voit la
 * seconde. Les mêler obligeait à lire les titres pour savoir sur quoi on
 * agissait.
 */
export function MonCompte(): React.JSX.Element {
  return <MemberSettingsInterne portee="compte" />;
}

/** La maison (US-57) : membres, rôles, création de compte. Propriétaire seul. */
export function GestionMembres(): React.JSX.Element {
  return <MemberSettingsInterne portee="maison" />;
}

/**
 * Le profil — prénom, nom, adresse (US-52).
 *
 * `PATCH /api/membres/:id` existait et était testé depuis US-42 : il n'avait
 * simplement **jamais eu de formulaire**. Il n'y avait donc, jusqu'ici, aucun
 * moyen de se renommer depuis l'application.
 *
 * L'enregistrement rafraîchit l'état de session : sans cela, « Connecté en tant
 * que Claire » resterait affiché jusqu'au prochain rechargement, et l'on
 * croirait l'enregistrement raté.
 */
function ProfileForm(
  { moi, onDone, onError }: {
    moi: NonNullable<SessionState['member']>;
    onDone: (m: string) => void;
    onError: (m: string) => void;
  },
): React.JSX.Element {
  const [firstName, setFirstName] = useState(moi.first_name);
  const [lastName, setLastName] = useState(moi.last_name ?? '');
  const [email, setEmail] = useState(moi.email ?? '');
  const [enCours, setEnCours] = useState(false);

  const modifie = firstName !== moi.first_name || lastName !== (moi.last_name ?? '') || email !== (moi.email ?? '');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (enCours) return;
    setEnCours(true);
    updateMember(moi.id, { first_name: firstName, last_name: lastName, email })
      .then(() => rafraichirSession())
      .then(() => { onDone('Profil enregistré.'); })
      .catch((e: unknown) => { onError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Prénom
          <TextInput
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); }}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-sm">
          {/* Un seul élément de flux, sinon le libellé et sa mention
              occupent **deux lignes** : le libellé est un `flex flex-col`, et
              un nœud de texte suivi d'un `<span>` y sont deux éléments
              empilés. Le champ « Nom » descendait donc d'une ligne par
              rapport à « Prénom » posé à côté de lui. */}
          <span>Nom <span className="text-foreground-muted">(facultatif)</span></span>
          <TextInput
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); }}
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        Adresse e-mail
        <TextInput
          type="email"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
        />
      </label>
      {/* Le prénom est ce que toute l'application affiche ; l'adresse est ce
          avec quoi on se connecte. Le dire évite qu'on cherche « son nom
          d'utilisateur » ailleurs. */}
      <p className="text-xs text-foreground-secondary">
        Votre prénom est le nom que le livre affiche à côté de vos recettes. Votre adresse est ce avec quoi
        vous vous connectez.
      </p>
      <div>
        <Button type="submit" variant="secondary" disabled={!modifie || enCours || firstName.trim() === ''}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function ChangePassword(
  { memberId, onDone, onError }: { memberId: string | null; onDone: (m: string) => void; onError: (m: string) => void },
): React.JSX.Element {
  const [password, setPassword] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (memberId === null) return;
    updateMember(memberId, { password })
      .then(() => {
        setPassword('');
        // Changer son mot de passe fait tomber ses propres sessions : c'est
        // l'attente évidente, et il faut donc se reconnecter.
        onDone('Mot de passe changé. Reconnectez-vous.');
      })
      .catch((e: unknown) => { onError(e instanceof Error ? e.message : String(e)); });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
      <label className="flex flex-1 flex-col gap-1 text-sm">
        Nouveau mot de passe
        <TextInput
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
        />
      </label>
      <Button type="submit" variant="secondary" disabled={password.length < 8}>Changer</Button>
    </form>
  );
}

/**
 * Les invitations en attente (US-103).
 *
 * Elles n'existaient pas : jusqu'ici, « ajouter quelqu'un » créait un compte
 * ou ne faisait rien, et il n'y avait pas d'état intermédiaire à montrer. Il y
 * en a un désormais, et c'est celui où l'on se demande « est-ce qu'elle a reçu
 * mon message ? ».
 *
 * **`sent_at` nulle ne veut pas dire « invitation invalide »** mais « le
 * message n'est pas parti » — sans SMTP, ou parce que Gmail a refusé. Le lien,
 * lui, fonctionne : c'est pour cela que la ligne le dit au lieu de le taire.
 */
function InvitationsEnAttente(
  { invitations, onDone, onError }: {
    invitations: PublicInvitation[];
    onDone: (m: string) => void;
    onError: (m: string) => void;
  },
): React.JSX.Element | null {
  if (invitations.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-heading-3">Invitations en attente</h3>
      <ul className="flex flex-col gap-2">
        {invitations.map((invitation) => (
          <li
            key={invitation.id}
            className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-surface-raised px-3 py-2"
          >
            <span>{invitation.first_name}</span>
            <span className="text-meta">{invitation.email}</span>
            <span className="text-sm text-foreground-secondary">{LIBELLES_ROLE[invitation.role]}</span>
            {invitation.sent_at === null && (
              <span className="text-sm text-foreground-secondary">
                message non envoyé — transmettez le lien vous-même
              </span>
            )}
            <span className="ml-auto">
              <Button
                variant="ghost"
                onClick={() => {
                  cancelInvitation(invitation.id)
                    .then(() => { onDone(`L’invitation de ${invitation.first_name} a été annulée.`); })
                    .catch((e: unknown) => { onError(e instanceof Error ? e.message : String(e)); });
                }}
              >
                Annuler
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {/* Réinviter la même adresse relance : le dire évite de chercher un
          bouton « renvoyer » qui n'existe pas, et évite surtout qu'on annule
          d'abord — ce qui marcherait aussi, mais pour rien. */}
      <p className="text-xs text-foreground-secondary">
        Pour relancer quelqu’un, invitez-le de nouveau avec la même adresse : le lien précédent cesse alors de
        fonctionner.
      </p>
    </div>
  );
}

/**
 * Inviter quelqu'un (US-103).
 *
 * **Aucun mot de passe n'est saisi ici.** C'était le défaut du formulaire
 * précédent : le propriétaire choisissait un secret pour quelqu'un d'autre. La
 * personne invitée pose le sien en arrivant, et personne d'autre ne le connaît.
 *
 * **Le lien est affiché après l'envoi, pas à sa place.** Le message part quand
 * l'instance sait envoyer, et le lien s'affiche de toute façon : dans une
 * famille, il tombe en indésirables une fois sur trois et la personne est
 * souvent dans la pièce d'à côté. `courrier` vient du serveur — la capacité est
 * annoncée, jamais supposée (US-53).
 */
function InviterQuelquun(
  { courrier, onDone, onError }: { courrier: boolean; onDone: (m: string) => void; onError: (m: string) => void },
): React.JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('membre');
  const [enCours, setEnCours] = useState(false);
  const [emise, setEmise] = useState<CreateInvitationResponse | null>(null);
  const [copie, setCopie] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (enCours) return;
    setEnCours(true);
    createInvitation({ first_name: firstName, last_name: lastName, email, role })
      .then((invitation) => {
        const prenom = firstName;
        setFirstName(''); setLastName(''); setEmail('');
        setEmise(invitation); setCopie(false);
        onDone(invitation.envoye
          ? `Un message vient de partir chez ${prenom}.`
          : `L’invitation de ${prenom} est prête. Envoyez-lui le lien ci-dessous.`);
      })
      .catch((e: unknown) => { onError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 rounded-card border border-border p-3">
      <h3 className="text-heading-3">Inviter quelqu’un</h3>

      {/* **La seule promesse que le système ne peut pas tenir** (US-49). Celui
          qui héberge l'instance possède le fichier de la base, les sauvegardes
          et le dossier des photos : il peut tout lire, quoi que dise
          l'interface. Le dire ici, avant de demander à quelqu'un d'y tenir un
          journal personnel, est une question de loyauté — et le seul remède
          réel serait un chiffrement qui casserait la recherche, les
          étiquettes, l'adaptation des quantités et le PDF.

          Depuis US-103, la même phrase est dite **à l'invité** sur son écran
          d'arrivée : celle-ci reste, parce que le propriétaire doit savoir ce
          qu'il engage au moment où il l'engage. */}
      <p className="text-xs text-foreground-secondary">
        À savoir : ses recettes privées et son journal ne seront visibles d’aucun autre membre, mais la
        personne qui héberge cette instance a accès au fichier de données, donc à tout. Aucune application ne
        peut promettre le contraire.
      </p>

      <div className="flex flex-wrap gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Prénom
          <TextInput
            type="text"
            autoComplete="off"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); }}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span>Nom <span className="text-foreground-muted">(facultatif)</span></span>
          <TextInput
            type="text"
            autoComplete="off"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); }}
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        Adresse e-mail
        <TextInput
          type="email"
          inputMode="email"
          autoComplete="off"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Rôle
        {/* `proprietaire` n'est pas proposé : on n'invite pas un propriétaire
            par courrier, et la base le refuse de toute façon. Un rôle se
            change ensuite, dans la liste ci-dessus. */}
        <Select value={role} onChange={(e) => { setRole(e.target.value as MemberRole); }}>
          {(['membre', 'lecture'] as const).map((valeur) => (
            <option key={valeur} value={valeur}>{LIBELLES_ROLE[valeur]}</option>
          ))}
        </Select>
      </label>
      <p className="text-xs text-foreground-secondary">
        {courrier
          ? 'Elle recevra un message avec un lien valable sept jours. Elle choisira son mot de passe elle-même : aucun mot de passe ne circule par courrier.'
          : 'Cette instance n’envoie pas de courrier : le lien s’affichera ici, à vous de le lui transmettre. Elle choisira son mot de passe elle-même.'}
      </p>
      <Button
        type="submit"
        variant="primary"
        disabled={enCours || firstName.trim() === '' || email.trim() === ''}
      >
        {courrier ? 'Inviter' : 'Créer le lien d’invitation'}
      </Button>

      {emise !== null && (
        <div className="flex flex-col gap-2 rounded-card border border-border-interactive p-3">
          <p className="text-sm">
            {emise.envoye
              ? `Message envoyé à ${emise.email}. S’il n’arrive pas, transmettez ce lien :`
              : `Transmettez ce lien à ${emise.email} :`}
          </p>
          {/* Affiché en clair et sélectionnable : la copie automatique échoue
              silencieusement hors HTTPS et sur certains navigateurs, et un
              lien qu'on ne peut pas lire est un lien qu'on ne peut pas
              rattraper. */}
          <p className="break-all text-meta">{emise.lien}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                navigator.clipboard.writeText(emise.lien)
                  .then(() => { setCopie(true); })
                  .catch(() => { setCopie(false); });
              }}
            >
              Copier le lien
            </Button>
            {copie && <span className="text-sm text-foreground-secondary">Copié.</span>}
          </div>
          {/* Une propriété, pas une précaution : le premier qui l'ouvre le
              consomme. Le dire évite qu'on le poste dans une conversation de
              groupe en pensant bien faire. */}
          <p className="text-xs text-foreground-secondary">
            Ce lien ne fonctionne qu’une fois, et expire dans sept jours. Il ne sera plus affiché après cette
            page.
          </p>
        </div>
      )}
    </form>
  );
}
